import { useEffect } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import { ensureSelectionPane, SELECTION_PANE } from './SelectionHalo.tsx'

const HIGHLIGHT_COLOR = '#f97316' // orange-500 — distinct from selection blue and layer palettes

export interface SearchHighlight {
  /** GeoJSON-shaped geometry from the search endpoint (Point or LineString). */
  geometry: { type: string; coordinates: number[] | number[][] }
  label: string
}

/**
 * Marks the feature picked from the search dropdown: a pulsing ring (points)
 * or a wide underlay polyline (feeder routes), plus a persistent label
 * tooltip so the searched equipment is identifiable among anonymous markers.
 * Lifecycle is owned by MapPage: replaced on a new search pick, cleared on
 * panel close or deselect (Escape / basemap click); a trace does NOT clear it.
 */
export function SearchHighlightOverlay({ highlight }: { highlight: SearchHighlight }) {
  const map = useMap()

  useEffect(() => {
    ensureSelectionPane(map)
    // preferCanvas on the MapContainer (D1) makes Leaflet default custom
    // panes to Canvas too, which ignores `className` — the .pulse-marker
    // animation would silently stop. Force this pane's shapes back onto an
    // explicit SVG renderer; created fresh per highlight and removed in
    // cleanup alongside layerGroup so it never leaks an empty <svg>.
    const renderer = L.svg({ pane: SELECTION_PANE })
    const layerGroup = L.layerGroup().addTo(map)
    const { geometry, label } = highlight

    if (geometry.type === 'Point') {
      const [lng, lat] = geometry.coordinates as number[]
      const latLng = L.latLng(lat, lng)
      // Continuously pulsing outer ring (.pulse-marker carries the
      // transform-box fix from index.css)
      L.circleMarker(latLng, {
        pane: SELECTION_PANE,
        renderer,
        radius: 16,
        color: HIGHLIGHT_COLOR,
        weight: 3,
        fill: false,
        opacity: 0.9,
        interactive: false,
        className: 'pulse-marker',
      }).addTo(layerGroup)
      // Static ring carrying the persistent label tooltip
      L.circleMarker(latLng, {
        pane: SELECTION_PANE,
        renderer,
        radius: 10,
        color: HIGHLIGHT_COLOR,
        weight: 2.5,
        fill: false,
        opacity: 1,
        interactive: false,
      })
        .bindTooltip(label, { permanent: true, direction: 'top', offset: [0, -12] })
        .addTo(layerGroup)
    } else if (geometry.type === 'LineString') {
      const latLngs = (geometry.coordinates as number[][]).map(([lng, lat]) =>
        L.latLng(lat, lng),
      )
      if (latLngs.length > 0) {
        L.polyline(latLngs, {
          pane: SELECTION_PANE,
          renderer,
          color: HIGHLIGHT_COLOR,
          weight: 8,
          opacity: 0.5,
          interactive: false,
        })
          .bindTooltip(label, { permanent: true, direction: 'top' })
          .addTo(layerGroup)
      }
    }

    return () => {
      layerGroup.remove()
      renderer.remove()
    }
  }, [highlight, map])

  return null
}
